import { LOCAL_STORAGE_KEYS } from "@config/constant";

/**
 * 汇报摘要条本地 watermark（V3.0.7 现成接口拼装期替身；GAP-1 服务端 watermark 方案就绪后下线）。
 *
 * - `hasEnteredReportToday`：复用 {n} new 徽标清除标记 key（BriefFeed 进汇报即清 →
 *   同日值即「当日已进过汇报」），不重复建 key
 * - task_ids 快照：fetchSummary 每次观察后写入；当日再次观察时差集 = new_count
 *   （「上次查看汇报后新出现的未读任务」的本地近似）
 */

type WatermarkEntry = {
  /** 本地自然日 yyyy-MM-dd */
  date: string;
  task_ids: string[];
};

type WatermarkMap = Record<string, WatermarkEntry>;

function toLocalIsoDate(d = new Date()): string {
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function readAll(): WatermarkMap {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(
      LOCAL_STORAGE_KEYS.REPORT_SUMMARY_WATERMARK,
    );
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as WatermarkMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: WatermarkMap): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.REPORT_SUMMARY_WATERMARK,
      JSON.stringify(map),
    );
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function readReportSummaryWatermark(
  ownerId: string,
): WatermarkEntry | null {
  if (!ownerId) {
    return null;
  }
  const entry = readAll()[ownerId];
  return entry && typeof entry === "object" && Array.isArray(entry.task_ids)
    ? entry
    : null;
}

export function writeReportSummaryWatermark(
  ownerId: string,
  taskIds: string[],
  date = toLocalIsoDate(),
): void {
  if (!ownerId) {
    return;
  }
  const all = readAll();
  all[ownerId] = { date, task_ids: taskIds };
  writeAll(all);
}

/** 当日已进过汇报（复用 BRIEF_NEW_INDICATOR_CLEARED：进汇报清 {n} new 时写入当日值） */
export function hasEnteredReportToday(date = toLocalIsoDate()): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      localStorage.getItem(LOCAL_STORAGE_KEYS.BRIEF_NEW_INDICATOR_CLEARED) ===
      date
    );
  } catch {
    return false;
  }
}
